import React, { useState, useMemo } from 'react'
import { PillTabs } from '../../FbUI/PillTabs'
import { TableLinkStyleProvider } from '../../Tables/CommonTableCells/TableLinkStyleContext'
import { UnmatchedDoorsTable } from '../../DataExpansionComponents/UnmatchedDoors/UnmatchedDoorsTable'
import { TabbedDoorSectionProps } from './types'
import { IContactCompany } from 'models/contact_companies'
import { FilterIdentifier } from 'models/saved_view'
import { formatInteger } from '../../../utils/formatting'
import { StandardSummaryResponse } from 'models/paginated-response'

interface DoorsTableTabsProps {
  forChainProxyId?: number
  chainID?: number
  chainData?: TabbedDoorSectionProps['chainData']
  subCategory: TabbedDoorSectionProps['subCategory']
  matchedTableComponent: TabbedDoorSectionProps['matchedTableComponent']
  matchedTableProps?: TabbedDoorSectionProps['matchedTableProps']
  matchedTableKey?: string
  unmatchedTableKeyPrefix: string
  className?: string
}

export function DoorsTableTabs({
  forChainProxyId,
  chainID,
  chainData,
  subCategory,
  matchedTableComponent: MatchedTableComponent,
  matchedTableProps = {},
  matchedTableKey,
  unmatchedTableKeyPrefix,
  className = '',
}: DoorsTableTabsProps) {
  const [selectedTab, setSelectedTab] = useState<'matched' | 'unmatched'>(
    'matched'
  )
  const [unmatchedCount, setUnmatchedCount] = useState(0)

  // Create chainProxy object for UnmatchedDoorsTable
  const chainProxy: IContactCompany | undefined = useMemo(() => {
    if (!chainData || !subCategory) return undefined

    return {
      id: forChainProxyId || chainData.contact_company_id || 0,
      domain: chainData.domain || '',
      name: chainData.name || subCategory.name,
      sub_category: subCategory,
    } as IContactCompany
  }, [chainData, subCategory, forChainProxyId])

  const handleUnmatchedCountChange = (summary: StandardSummaryResponse) => {
    setUnmatchedCount(summary.alt_count || 0)
  }

  const totalCount = chainData?.count || 0
  const unmatchedTableKey = `${unmatchedTableKeyPrefix}-${chainData?.id}`
  const tabIdPrefix = `doors-tabs-${chainData?.id ?? 'modal'}`

  return (
    <div className={`w-full ${className}`}>
      <PillTabs<'matched' | 'unmatched'>
        tabs={[
          {
            key: 'matched',
            title: `First Bite (${formatInteger(totalCount)})`,
          },
          {
            key: 'unmatched',
            title: `Unmatched (${formatInteger(unmatchedCount)})`,
          },
        ]}
        activeKey={selectedTab}
        onChange={setSelectedTab}
        idPrefix={tabIdPrefix}
        aria-label={`${chainData?.name ?? 'Chain'} doors`}
      />

      {/* Both panels stay mounted (rather than <TabPanel>, which unmounts the
          inactive panel) so the Unmatched table can report its row count into
          the tab label before that tab is ever opened. We still wire the
          tabpanel ARIA by hand and toggle visibility with Tailwind. */}
      <TableLinkStyleProvider variant="plain">
        <div
          role="tabpanel"
          id={`${tabIdPrefix}-panel-matched`}
          aria-labelledby={`${tabIdPrefix}-tab-matched`}
          tabIndex={0}
          className={`mt-4 max-h-[600px] overflow-hidden rounded-lg border border-neutral-200 ${
            selectedTab === 'matched' ? 'flex' : 'hidden'
          }`}
        >
          <MatchedTableComponent
            chainID={chainID}
            forChainProxyId={forChainProxyId}
            chainData={chainData}
            chainProxy={chainProxy}
            unmatchedTableKey={unmatchedTableKey}
            tableKey={
              matchedTableKey || `${subCategory.slug}-table-${chainData?.id}`
            }
            {...matchedTableProps}
          />
        </div>

        <div
          role="tabpanel"
          id={`${tabIdPrefix}-panel-unmatched`}
          aria-labelledby={`${tabIdPrefix}-tab-unmatched`}
          tabIndex={0}
          className={`mt-4 max-h-[600px] overflow-hidden rounded-lg border border-neutral-200 ${
            selectedTab === 'unmatched' ? 'flex' : 'hidden'
          }`}
        >
          {chainProxy && (
            <UnmatchedDoorsTable
              tableKey={unmatchedTableKey}
              filterIdentifierModifier={
                chainProxy.id.toString() as FilterIdentifier
              }
              chainProxy={chainProxy}
              setTotalRowsCount={handleUnmatchedCountChange}
            />
          )}
        </div>
      </TableLinkStyleProvider>
    </div>
  )
}
